"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight, ChevronDown, Menu } from "lucide-react";
import { enquiryHref, primaryNav } from "@/lib/site";
import type { CourseMenu } from "@/lib/nav";
import { readSession } from "@/lib/portal/session";
import type { Session } from "@/lib/portal/types";
import { cn } from "@/lib/utils";
import { Container } from "@/components/ui/Container";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/Button";
// The drawer is only needed once the menu is opened, so its code is fetched on first tap (or hover/touch) instead of with the page.
type MobileMenuType = typeof import("./MobileMenu").MobileMenu;
const loadMobileMenu = () => import("./MobileMenu").then((m) => m.MobileMenu);

const FLOATING_HEADER_PATHS = ["/home2"];
const BURGUNDY_HEADER_PATHS = ["/home5"]; // solid burgundy bar with light text
const NIGHT_HEADER_PATHS = ["/home7"]; // near-black bar with a gold hairline (dark theme)
const OVERLAY_HEADER_PATHS = ["/home8"]; // transparent bar laid over the hero image (solid once you scroll)
const BLUSH_HEADER_PATHS = ["/home12"]; // solid burgundy bar in the Enquiry-button colour (matches the Home 12 footer)
// all other pages: white bar at the top, turns cream once you scroll (the default-home header)
const GOLD_HEADER_PATHS = ["/home6"]; // solid gold (logo yellow) bar with dark text

export function Header({ courseMenu }: { courseMenu: CourseMenu }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [MobileMenu, setMobileMenu] = useState<MobileMenuType | null>(null); // stays mounted after first open, so its close animation still plays
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const coursesRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // keep the Login / Dashboard button in sync with the portal session
  useEffect(() => {
    const sync = () => setSession(readSession());
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [pathname]);

  // close the courses dropdown when the route changes
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    setCoursesOpen(false);
  }

  useEffect(() => {
    if (!coursesOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (coursesRef.current && !coursesRef.current.contains(e.target as Node)) setCoursesOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCoursesOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [coursesOpen]);

  // pages that use the floating "pill" header instead of the full-width bar
  const floating = FLOATING_HEADER_PATHS.includes(pathname);
  const night = NIGHT_HEADER_PATHS.includes(pathname);
  const overlay = OVERLAY_HEADER_PATHS.includes(pathname);
  const blush = BLUSH_HEADER_PATHS.includes(pathname);
  const dark = BURGUNDY_HEADER_PATHS.includes(pathname) || night || overlay || blush;
  const gold = GOLD_HEADER_PATHS.includes(pathname);

  const isActive = (href: string) => pathname === href || pathname.startsWith(href + "/");

  const linkClass = (active: boolean) =>
    cn(
      "relative inline-flex items-center gap-1 rounded-lg px-3 py-2 text-[15px] font-medium transition-colors",
      gold
        ? active
          ? "text-brand-900"
          : "text-brand-700 hover:text-brand-900"
        : dark
        ? active
          ? "text-gold-300"
          : "text-white/85 hover:text-gold-300"
        : active
          ? "text-brand-700"
          : "text-ink-soft hover:text-brand-700"
    );
  const underline = (active: boolean) => (
    <span
      className={cn(
        gold ? "bg-brand-700" : "bg-gold-500",
        "absolute inset-x-3 -bottom-0.5 h-0.5 origin-left rounded-full transition-transform duration-300",
        active ? "scale-x-100" : "scale-x-0"
      )}
      aria-hidden
    />
  );

  return (
    <header
      className={cn(
        overlay ? "fixed inset-x-0 top-0 z-50 w-full transition-all duration-300" : "sticky z-50 w-full transition-all duration-300",
        overlay
          ? cn(
              scrolled
                ? "border-b border-white/10 bg-brand-900/90 shadow-[0_10px_30px_-16px_rgba(0,0,0,0.55)] backdrop-blur-md"
                : "border-b border-transparent bg-transparent"
            )
          : floating
          ? cn("top-0 px-3 py-3 sm:px-5", scrolled ? "bg-transparent" : "bg-white")
          : gold
            ? cn("top-0 border-b border-brand-900/10 bg-gold-400", scrolled && "shadow-[0_10px_30px_-16px_rgba(78,18,38,0.45)]")
          : dark
            ? cn(
                "top-0 border-b",
                night ? "border-gold-400/30 bg-[#130b0e]" : blush ? "border-white/10 bg-brand-600" : "border-white/10 bg-brand-700",
                scrolled && "shadow-[0_10px_30px_-16px_rgba(0,0,0,0.55)]"
              )
          : cn(
              "top-0",
              scrolled
                ? "border-b border-line bg-cream/95 backdrop-blur-md shadow-[0_1px_20px_-12px_rgba(78,18,38,0.25)]"
                : "border-b border-line bg-white"
            )
      )}
    >
      <HeaderShell floating={floating} scrolled={scrolled}>
        <div className={cn("flex items-center justify-between gap-4", floating ? "h-16" : "h-[68px]")}>
          {dark || gold ? (
            <span className="inline-flex rounded-xl bg-cream px-2.5 py-1">
              <Logo priority />
            </span>
          ) : (
            <Logo priority />
          )}

          <nav aria-label="Primary" className="hidden items-center gap-0.5 min-[1221px]:flex">
            {primaryNav.map((item) =>
              item.label === "Courses" ? (
                <div key={item.href} ref={coursesRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setCoursesOpen((v) => !v)}
                    aria-haspopup="true"
                    aria-expanded={coursesOpen}
                    className={linkClass(isActive(item.href))}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn("h-4 w-4 transition-transform duration-200", coursesOpen && "rotate-180")}
                    />
                    {underline(isActive(item.href) || coursesOpen)}
                  </button>
                  {/* dynamic course dropdown - built from the course data */}
                  <div
                    className={cn(
                      "absolute left-1/2 top-full z-50 w-[46rem] max-w-[92vw] -translate-x-1/2 pt-3 transition-all duration-200",
                      coursesOpen ? "visible opacity-100 translate-y-0" : "invisible pointer-events-none opacity-0 -translate-y-1"
                    )}
                  >
                    <div className="rounded-2xl border border-line bg-white p-5 shadow-lift">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        {courseMenu.groups.map((g) => (
                          <div key={g.category}>
                            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-gold-600">{g.category}</div>
                            <ul className="mt-2 space-y-0.5">
                              {g.items.map((c) => (
                                <li key={c.slug}>
                                  <Link
                                    href={`/courses/${c.slug}`}
                                    onClick={() => setCoursesOpen(false)}
                                    className="block rounded-lg px-2 py-1.5 text-sm leading-snug text-ink-soft transition-colors hover:bg-brand-50 hover:text-brand-700"
                                  >
                                    {c.title}
                                  </Link>
                                </li>
                              ))}
                              {g.more > 0 && (
                                <li>
                                  <Link
                                    href={`/courses?category=${encodeURIComponent(g.category)}`}
                                    onClick={() => setCoursesOpen(false)}
                                    className="block rounded-lg px-2 py-1.5 text-sm font-semibold leading-snug text-brand-700 transition-colors hover:bg-brand-50"
                                  >
                                    +{g.more} more
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <Link
                        href="/courses"
                        onClick={() => setCoursesOpen(false)}
                        className="mt-5 flex items-center justify-between rounded-xl bg-brand-50 px-4 py-3 text-sm font-semibold text-brand-700 hover:bg-brand-100"
                      >
                        View all courses{courseMenu.hiddenCategories > 0 && ` · +${courseMenu.hiddenCategories} more categories`} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  className={linkClass(isActive(item.href))}
                >
                  {item.label}
                  {underline(isActive(item.href))}
                </Link>
              )
            )}
          </nav>

          <div className="hidden items-center gap-2 min-[1221px]:flex">
            <Button
              href={session ? "/dashboard" : "/login"}
              size="sm"
              variant={dark ? "dark" : gold ? "secondary" : "outline"}
            >
              {session ? `Dashboard${session.name ? ` · ${session.name.split(" ")[0]}` : ""}` : "Login"}
            </Button>
            <Button
              href={enquiryHref}
              size="sm"
              withArrow
              variant={dark ? "gold" : "primary"}
            >
              Enquire Now
            </Button>
          </div>

          <button
            className={cn(
              "inline-flex h-11 w-11 items-center justify-center rounded-xl border min-[1221px]:hidden",
              gold ? "border-brand-900/30 bg-brand-900/10 text-brand-900" : dark ? "border-white/30 bg-white/10 text-white" : "border-line bg-white/60 text-ink"
            )}
            onPointerEnter={() => { if (!MobileMenu) loadMobileMenu().then((C) => setMobileMenu(() => C)); }}
            onClick={() => {
              setOpen(true);
              if (!MobileMenu) loadMobileMenu().then((C) => setMobileMenu(() => C));
            }}
            aria-label="Open menu"
            aria-expanded={open}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </HeaderShell>

      {MobileMenu && <MobileMenu open={open} onClose={() => setOpen(false)} courseMenu={courseMenu} session={session} />}
    </header>
  );
}

function HeaderShell({
  floating,
  scrolled,
  children,
}: {
  floating: boolean;
  scrolled: boolean;
  children: React.ReactNode;
}) {
  if (!floating) return <Container>{children}</Container>;
  return (
    <div
      className={cn(
        "mx-auto w-full max-w-[1240px] rounded-full border bg-white/90 px-4 backdrop-blur-md transition-shadow duration-300 sm:px-7",
        scrolled
          ? "border-line shadow-[0_18px_40px_-20px_rgba(78,18,38,0.45)]"
          : "border-line/70 shadow-[0_10px_30px_-22px_rgba(78,18,38,0.35)]"
      )}
    >
      {children}
    </div>
  );
}
